import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PenLine, Eraser, BadgeCheck, Loader2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { ChapterHead, Reveal, EASE } from "./motion";
import { OFFER, OPTION, fmt } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const accentColor = () => {
  const v = getComputedStyle(document.documentElement).getPropertyValue("--c-accent").trim();
  return `rgb(${v.split(/\s+/).join(", ")})`;
};

export const Acceptance = () => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [signed, setSigned] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = c.offsetWidth * 2;
    c.height = c.offsetHeight * 2;
    const ctx = c.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - r.left, y: p.clientY - r.top };
  };
  const start = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = accentColor();
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setSigned(true);
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setSigned(false);
  };

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = signed && name.trim() && validEmail && !sending && !accepted;

  const submit = async () => {
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/acceptances`, {
        option_id: OPTION.id,
        name: name.trim(),
        email: email.trim(),
        signature: canvasRef.current.toDataURL("image/png"),
      });
      setAccepted(true);
    } catch (err) {
      setError("Could not save your acceptance. Please try again — or use Print / PDF and sign manually.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="chapter-acceptance" className="chapter-print-break bg-alt py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ChapterHead
          num="08"
          title="Acceptance & Authorization"
          sub="Sign below to convert this offer into a binding statement of work."
        />
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="space-y-8 rounded-sm border border-line bg-surface p-8 sm:p-10">
              <div className="rounded-sm border border-amber-500/50 bg-amber-500/10 px-5 py-4">
                <p className="font-mono2 text-[10px] tracking-[0.25em] text-accent uppercase">Engagement</p>
                <p className="font-display mt-1 text-base font-semibold text-ink">
                  {OPTION.name} — AED {fmt(OPTION.total)} · No VAT
                </p>
                <p className="mt-0.5 text-xs text-ink4">
                  Special rate — reduced from AED {fmt(OPTION.originalBase)}
                </p>
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="signatory-name" className="font-mono2 mb-3 block text-[11px] tracking-[0.3em] text-ink5 uppercase">
                    Authorized Signatory
                  </label>
                  <input
                    id="signatory-name"
                    data-testid="signatory-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name & title"
                    className="w-full border-b border-line bg-transparent py-3 font-display text-base text-ink placeholder:text-ink6 focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="signatory-email" className="font-mono2 mb-3 block text-[11px] tracking-[0.3em] text-ink5 uppercase">
                    Email
                  </label>
                  <input
                    id="signatory-email"
                    data-testid="signatory-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    className="w-full border-b border-line bg-transparent py-3 font-display text-base text-ink placeholder:text-ink6 focus:border-amber-500 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-mono2 flex items-center gap-2 text-[11px] tracking-[0.3em] text-ink5 uppercase">
                    <PenLine className="h-3.5 w-3.5" /> Signature
                  </span>
                  <button
                    data-testid="signature-clear-button"
                    onClick={clear}
                    className="print-hidden flex items-center gap-1.5 text-xs text-ink5 transition-colors duration-200 hover:text-accent"
                  >
                    <Eraser className="h-3.5 w-3.5" /> Clear
                  </button>
                </div>
                <canvas
                  ref={canvasRef}
                  data-testid="signature-canvas"
                  onMouseDown={start}
                  onMouseMove={move}
                  onMouseUp={() => (drawing.current = false)}
                  onMouseLeave={() => (drawing.current = false)}
                  onTouchStart={start}
                  onTouchMove={move}
                  onTouchEnd={() => (drawing.current = false)}
                  className="h-40 w-full cursor-crosshair rounded-sm border border-dashed border-line2 bg-base touch-none"
                />
              </div>
              {error && (
                <p data-testid="acceptance-error" className="flex items-center gap-2 rounded-sm border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                  <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
                </p>
              )}
              <button
                data-testid="accept-offer-button"
                disabled={!canSubmit}
                onClick={submit}
                className="print-hidden flex w-full items-center justify-center gap-2 rounded-full bg-amber-500 py-4 text-sm font-bold tracking-widest text-[#0b0f17] uppercase transition-[background-color,opacity,transform] duration-300 hover:bg-amber-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-30"
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                {sending ? "Submitting…" : `Accept Offer — AED ${fmt(OPTION.total)} · No VAT`}
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="flex h-full flex-col justify-between rounded-sm border border-line bg-surface p-8 sm:p-10">
              <div className="space-y-6">
                <div>
                  <p className="font-mono2 mb-1 text-[10px] tracking-[0.3em] text-ink5 uppercase">Issued By</p>
                  <p className="font-display text-lg font-semibold text-ink">{OFFER.consultant}</p>
                  <p className="text-sm text-ink4">{OFFER.consultantRole}</p>
                </div>
                <div>
                  <p className="font-mono2 mb-1 text-[10px] tracking-[0.3em] text-ink5 uppercase">Issued To</p>
                  <p className="font-display text-lg font-semibold text-ink">{OFFER.client}</p>
                  {OFFER.contacts.map((c) => (
                    <p key={c.name} className="text-sm text-ink4">
                      {c.name} · {c.role}
                    </p>
                  ))}
                </div>
                <div className="flex gap-12">
                  <div>
                    <p className="font-mono2 mb-1 text-[10px] tracking-[0.3em] text-ink5 uppercase">Reference</p>
                    <p className="text-sm font-semibold text-ink2">{OFFER.ref}</p>
                  </div>
                  <div>
                    <p className="font-mono2 mb-1 text-[10px] tracking-[0.3em] text-ink5 uppercase">Date</p>
                    <p className="text-sm font-semibold text-ink2">{OFFER.date}</p>
                  </div>
                </div>
              </div>
              <AnimatePresence>
                {accepted && (
                  <motion.div
                    data-testid="acceptance-confirmation"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="mt-10 flex items-center gap-4 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-6 py-5"
                  >
                    <BadgeCheck className="h-8 w-8 shrink-0 text-emerald-400" />
                    <div>
                      <p className="font-display font-semibold text-emerald-300">
                        {OPTION.name} accepted — AED {fmt(OPTION.total)}
                      </p>
                      <p className="text-sm text-emerald-400/80">
                        Thank you{name ? `, ${name}` : ""}. A confirmation email is on its way to you,
                        and Mohamed has been notified. A formal agreement will follow within 24 hours.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
        <Reveal className="mt-24">
          <footer className="border-t border-line pt-10 pb-4 text-center">
            <p className="font-mono2 text-[11px] tracking-[0.35em] text-ink6 uppercase">
              {OFFER.consultant} × {OFFER.client} · {OFFER.ref} · {OFFER.date}
            </p>
          </footer>
        </Reveal>
      </div>
    </section>
  );
};
